import { useState } from 'react';
import { useRecoilCallback } from 'recoil';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { dcmjsSetString } from '~/domain/services/dicomProcessor';
import { getStructureSetRepository } from '~/domain/ServiceContainer';
import { Image3D } from '~/domain/patient/image3d';
import { StructureSet } from '~/domain/patient/structureset';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { structureSetReadModelFamily } from '~/state/atoms/structureSet/structureSetAtoms';

export type ImageSeriesEdits = {
    seriesDescription?: string;
    seriesNumber?: number;
};

export type StructureSetEdits = {
    label?: string;
    approvalStatus?: DICOMApprovalStatus;
    reviewerName?: string;
};

export type RoiFieldEdits = {
    name?: string;
    interpretedType?: RoiInterpretedType;
};

export type RoiEdits = Record<string, Record<number, RoiFieldEdits>>;

type UseExportEditingResult = {
    imageEdits: Record<string, ImageSeriesEdits>;
    ssEdits: Record<string, StructureSetEdits>;
    roiEdits: RoiEdits;
    setImageSeriesDescription: (uid: string, description: string) => void;
    setImageSeriesNumber: (uid: string, seriesNumber: number) => void;
    setSsLabel: (uid: string, label: string) => void;
    setSsApproval: (uid: string, status: DICOMApprovalStatus) => void;
    setSsReviewer: (uid: string, name: string) => void;
    setRoiName: (ssUid: string, roiNumber: number, name: string) => void;
    setRoiType: (
        ssUid: string,
        roiNumber: number,
        type: RoiInterpretedType
    ) => void;
    commitAllEdits: () => void;
};

const useExportEditing = (
    exportableImages: Image3D[],
    exportableStructureSets: StructureSet[]
): UseExportEditingResult => {
    const [imageEdits, setImageEdits] = useState<
        Record<string, ImageSeriesEdits>
    >({});
    const [ssEdits, setSsEdits] = useState<Record<string, StructureSetEdits>>(
        {}
    );
    const [roiEdits, setRoiEdits] = useState<RoiEdits>({});

    const setImageSeriesDescription = (uid: string, description: string) =>
        setImageEdits((prev) => ({
            ...prev,
            [uid]: { ...prev[uid], seriesDescription: description },
        }));

    const setImageSeriesNumber = (uid: string, seriesNumber: number) =>
        setImageEdits((prev) => ({
            ...prev,
            [uid]: { ...prev[uid], seriesNumber },
        }));

    const setSsLabel = (uid: string, label: string) =>
        setSsEdits((prev) => ({ ...prev, [uid]: { ...prev[uid], label } }));

    const setSsApproval = (uid: string, status: DICOMApprovalStatus) =>
        setSsEdits((prev) => ({
            ...prev,
            [uid]: {
                ...prev[uid],
                approvalStatus: status,
                // Clear reviewer when switching back to UNAPPROVED
                reviewerName:
                    status === DICOMApprovalStatus.UNAPPROVED
                        ? undefined
                        : prev[uid]?.reviewerName,
            },
        }));

    const setSsReviewer = (uid: string, name: string) =>
        setSsEdits((prev) => ({
            ...prev,
            [uid]: { ...prev[uid], reviewerName: name },
        }));

    const setRoiName = (ssUid: string, roiNumber: number, name: string) =>
        setRoiEdits((prev) => ({
            ...prev,
            [ssUid]: {
                ...prev[ssUid],
                [roiNumber]: { ...prev[ssUid]?.[roiNumber], name },
            },
        }));

    const setRoiType = (
        ssUid: string,
        roiNumber: number,
        type: RoiInterpretedType
    ) =>
        setRoiEdits((prev) => ({
            ...prev,
            [ssUid]: {
                ...prev[ssUid],
                [roiNumber]: {
                    ...prev[ssUid]?.[roiNumber],
                    interpretedType: type,
                },
            },
        }));

    const commitAllEdits = useRecoilCallback(
        ({ set }) =>
            () => {
                for (const image of exportableImages) {
                    const edits = imageEdits[image.dicomUid];
                    if (!edits) {
                        continue;
                    }
                    for (const slice of image.imageSlices) {
                        if (edits.seriesDescription !== undefined) {
                            dcmjsSetString(
                                slice.datasetWrapper,
                                'SeriesDescription',
                                edits.seriesDescription
                            );
                        }
                        if (edits.seriesNumber !== undefined) {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            (slice.datasetWrapper.dataset as any).SeriesNumber =
                                edits.seriesNumber;
                        }
                    }
                }

                const repo = getStructureSetRepository();
                for (const ss of exportableStructureSets) {
                    if (!repo.exists(ss.dicomUid)) {
                        continue;
                    }

                    const edits = ssEdits[ss.dicomUid];
                    const rois = roiEdits[ss.dicomUid];
                    let updatedReadModel: StructureSetReadModel | undefined;

                    // Step 1: Apply SS label before approval status.
                    // _ensureCanModify() blocks label edits on approved sets.
                    if (edits?.label !== undefined) {
                        const label = edits.label;
                        updatedReadModel = repo.updateStructureSet(
                            ss.dicomUid,
                            (s) => {
                                s.structureSetLabel = label;
                            }
                        );
                    }

                    // Step 2: Apply ROI name/type edits before SS approval
                    // status. Once the SS is APPROVED in the DICOM dataset,
                    // _ensureCanModify() in DicomRoi blocks all ROI name
                    // modifications.
                    if (rois) {
                        for (const [roiNumber, fieldEdits] of Object.entries(
                            rois
                        )) {
                            updatedReadModel = repo.updateRoi(
                                ss.dicomUid,
                                Number(roiNumber),
                                (roi) => {
                                    if (fieldEdits.name !== undefined) {
                                        // Clear ROI-level approval before
                                        // renaming: any reviewed ROI (APPROVED
                                        // or REJECTED) renamed before export
                                        // can no longer be considered reviewed.
                                        if (
                                            roi.approvalStatus !==
                                            DICOMApprovalStatus.UNAPPROVED
                                        ) {
                                            roi.approvalStatus =
                                                DICOMApprovalStatus.UNAPPROVED;
                                        }
                                        roi.name = fieldEdits.name;
                                    }
                                    if (
                                        fieldEdits.interpretedType !== undefined
                                    ) {
                                        roi.interpretedType =
                                            fieldEdits.interpretedType;
                                    }
                                }
                            );
                        }
                    }

                    // Step 3: Apply SS approval status, cascading to all ROIs.
                    // Renamed ROIs are excluded from the cascade: renaming
                    // after review means the structure is no longer considered
                    // reviewed (UNAPPROVED). If only the reviewer name changed
                    // (no approval status edit) and the SS is already APPROVED,
                    // update the reviewer without cascading ROIs.
                    if (
                        edits?.approvalStatus === undefined &&
                        edits?.reviewerName !== undefined &&
                        ss.approvalStatus === DICOMApprovalStatus.APPROVED
                    ) {
                        const reviewerName = edits.reviewerName;
                        updatedReadModel = repo.updateStructureSet(
                            ss.dicomUid,
                            (s) => {
                                s.setApprovalStatus(
                                    DICOMApprovalStatus.APPROVED,
                                    reviewerName,
                                    false
                                );
                            }
                        );
                    }
                    if (edits?.approvalStatus !== undefined) {
                        const approvalStatus = edits.approvalStatus;
                        const reviewerName = edits.reviewerName ?? '';
                        const renamedRoiNumbers = new Set<number>(
                            rois
                                ? Object.entries(rois)
                                      .filter(([, fe]) => fe.name !== undefined)
                                      .map(([n]) => Number(n))
                                : []
                        );
                        updatedReadModel = repo.updateStructureSet(
                            ss.dicomUid,
                            (s) => {
                                // Use setApprovalStatus to ensure ReviewerName,
                                // ReviewDate, and ReviewTime are updated
                                // atomically in the DICOM dataset.
                                s.setApprovalStatus(
                                    approvalStatus,
                                    reviewerName,
                                    false
                                );
                                // Cascade to all ROIs except renamed ones.
                                s.rois.forEach((roi) => {
                                    if (!renamedRoiNumbers.has(roi.roiNumber)) {
                                        roi.approvalStatus = approvalStatus;
                                    }
                                });
                            }
                        );
                    }

                    // Sync back to Recoil so the contouring step reflects
                    // changes made in the export window.
                    if (updatedReadModel !== undefined) {
                        set(
                            structureSetReadModelFamily(ss.dicomUid),
                            updatedReadModel
                        );
                    }
                }
            },
        [
            exportableImages,
            exportableStructureSets,
            imageEdits,
            ssEdits,
            roiEdits,
        ]
    );

    return {
        imageEdits,
        ssEdits,
        roiEdits,
        setImageSeriesDescription,
        setImageSeriesNumber,
        setSsLabel,
        setSsApproval,
        setSsReviewer,
        setRoiName,
        setRoiType,
        commitAllEdits,
    };
};

export default useExportEditing;
